import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Save, Trash2, X, Plus, Link2, Paperclip, MessageSquare, Activity,
  CheckSquare, Square, Image as ImageIcon, ExternalLink, ChevronDown, ChevronRight, Clock,
} from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { neonQuery } from '../../services/api';
import { getCurrentUser } from '../../services/auth';
import MediaPicker from '../../components/MediaPicker';
import { STATUSES, PRIORITIES, STATUS_BY_ID, PRIORITY_BY_ID } from './constants';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'code-block'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'link', 'code-block'];

const COMMENT_MODULES = { toolbar: [['bold', 'italic'], [{ list: 'bullet' }, { list: 'ordered' }], ['link']] };
const COMMENT_FORMATS = ['bold', 'italic', 'list', 'link'];

const r2Url = (key) => key ? `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}` : null;

function fmt(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function dueChip(dueDate) {
  if (!dueDate) return null;
  const d = new Date(dueDate);
  const today0 = new Date(); today0.setHours(0,0,0,0);
  const days = Math.round((d - today0) / 86400000);
  return { txt: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), overdue: days < 0, soon: days >= 0 && days <= 2 };
}

const FIELD_LABEL = { padding: '4px 0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-500)', letterSpacing: 0.5 };
const SELECT = { padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none', background: '#fff', minWidth: 120 };

export default function TaskDetail({
  taskId,
  areas = [],
  epics = [],
  assignableUsers = [],
  onUpdated,
  onDeleted,
}) {
  const user = getCurrentUser();
  const [data, setData] = useState(null); // { task, subtasks, comments, links, attachments, activity }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [titleEdit, setTitleEdit] = useState('');
  const [descEdit, setDescEdit] = useState('');
  const [descDirty, setDescDirty] = useState(false);

  const [showActivity, setShowActivity] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [showSubtasks, setShowSubtasks] = useState(true);
  const [showLinks, setShowLinks] = useState(true);
  const [showAttachments, setShowAttachments] = useState(true);

  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '' });
  const [newSubtask, setNewSubtask] = useState('');

  const load = useCallback(async () => {
    if (!taskId) { setData(null); return; }
    setLoading(true);
    try {
      // Handler returns { rows: { task, subtasks, comments, ... } }; neonQuery
      // unwraps to the inner object since `rows` is set.
      const r = await neonQuery('cp_tasks_get', { id: taskId });
      setData(r);
      setTitleEdit(r?.task?.title || '');
      setDescEdit(r?.task?.description || '');
      setDescDirty(false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const task = data?.task;

  const patch = async (fields) => {
    if (!task) return;
    setSaving(true);
    try {
      await neonQuery('cp_tasks_upsert', {
        id: task.id,
        ...fields,
        actor_id: user?.id,
        actor_name: user?.staffName,
      });
      await load();
      onUpdated?.();
    } catch (e) { console.error(e); alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const saveTitle = async () => {
    const t = titleEdit.trim();
    if (!t || t === task.title) return;
    await patch({ title: t });
  };
  const saveDescription = async () => {
    if (!descDirty) return;
    await patch({ description: descEdit });
    setDescDirty(false);
  };

  const postComment = async () => {
    const body = newComment.trim();
    // Quill emits empty content as <p><br></p>; treat that as empty.
    const stripped = body.replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, '').trim();
    if (!stripped) return;
    setPostingComment(true);
    try {
      await neonQuery('cp_task_comments_upsert', {
        task_id: task.id,
        admin_id: user?.id,
        admin_name: user?.staffName,
        body: newComment,
      });
      setNewComment('');
      await load();
      onUpdated?.();
    } catch (e) { console.error(e); alert('Comment failed: ' + e.message); }
    finally { setPostingComment(false); }
  };

  const deleteComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    await neonQuery('cp_task_comments_delete', { id });
    await load();
  };

  const addLink = async () => {
    if (!newLink.url.trim()) return;
    await neonQuery('cp_task_links_upsert', {
      task_id: task.id,
      label: newLink.label.trim() || null,
      url: newLink.url.trim(),
    });
    setNewLink({ label: '', url: '' });
    await load();
  };

  const deleteLink = async (id) => {
    await neonQuery('cp_task_links_delete', { id });
    await load();
  };

  const onPickAttachments = async (assets) => {
    const list = Array.isArray(assets) ? assets : [assets];
    if (!list.length) return;
    await neonQuery('cp_task_attachments_link', {
      task_id: task.id,
      mediaAssetIds: list.map(a => a.id),
    });
    setPickerOpen(false);
    await load();
  };

  const unlinkAttachment = async (id) => {
    await neonQuery('cp_task_attachments_unlink', { id });
    await load();
  };

  const addSubtask = async () => {
    const t = newSubtask.trim();
    if (!t) return;
    await neonQuery('cp_tasks_upsert', {
      title: t,
      area_id: task.area_id,
      parent_task_id: task.id,
      status: 'planned',
      priority: 'medium',
      actor_id: user?.id,
      actor_name: user?.staffName,
    });
    setNewSubtask('');
    await load();
  };

  const toggleSubtaskDone = async (sub) => {
    await neonQuery('cp_tasks_upsert', {
      id: sub.id,
      status: sub.status === 'done' ? 'planned' : 'done',
      actor_id: user?.id,
      actor_name: user?.staffName,
    });
    await load();
  };

  const deleteTask = async () => {
    if (!window.confirm(`Delete "${task.title}"? This will also delete its subtasks and comments. Cannot be undone.`)) return;
    await neonQuery('cp_tasks_delete', { id: task.id });
    onDeleted?.(task.id);
  };

  if (!taskId) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>
        Pick a task on the left to see details.
      </div>
    );
  }
  if (loading || !task) {
    return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Loading…</div>;
  }

  const status = STATUS_BY_ID[task.status] || STATUS_BY_ID.backlog;
  const priority = PRIORITY_BY_ID[task.priority] || PRIORITY_BY_ID.medium;
  const due = dueChip(task.due_date);

  return (
    <div style={{ padding: '16px 24px 60px 24px', maxWidth: 880 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>
            #{task.id}
            {task.epic_name && (
              <> · <span style={{ color: task.epic_color || 'var(--orange)' }}>
                {task.epic_color && <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: task.epic_color, marginRight: 4 }} />}
                {task.epic_name}
              </span></>
            )}
            {task.area_name && <> · <span style={{ color: 'var(--navy)' }}>{task.area_name}</span></>}
            {task.parent_task_id && <> · <em>subtask</em></>}
          </div>
          <input
            value={titleEdit}
            onChange={e => setTitleEdit(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            style={{
              width: '100%', padding: 4, fontSize: 22, fontWeight: 700, color: 'var(--navy)',
              border: 'none', outline: 'none', background: 'transparent',
              borderBottom: '2px solid transparent',
            }}
            onFocus={e => { e.currentTarget.style.borderBottomColor = 'var(--orange)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderBottomColor = 'transparent'; }}
          />
        </div>
        <button
          onClick={deleteTask}
          title="Delete task"
          style={{
            padding: '6px 10px', background: 'transparent',
            border: '1px solid var(--gray-300)', borderRadius: 6, color: '#dc2626',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
          }}>
          <Trash2 size={13} /> Delete
        </button>
      </div>

      {/* Meta strip */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        padding: '10px 14px', background: '#fafbfc', borderRadius: 8, marginBottom: 18,
      }}>
        <div>
          <div style={FIELD_LABEL}>Status</div>
          <select
            value={task.status}
            onChange={e => patch({ status: e.target.value })}
            style={{ ...SELECT, color: status.color, fontWeight: 700 }}>
            {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <div style={FIELD_LABEL}>Priority</div>
          <select
            value={task.priority}
            onChange={e => patch({ priority: e.target.value })}
            style={{ ...SELECT, color: priority.color, fontWeight: 700 }}>
            {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <div style={FIELD_LABEL}>Assignee</div>
          <select
            value={task.assignee_id || ''}
            onChange={e => {
              const id = e.target.value || null;
              const u = assignableUsers.find(x => String(x.admin_id) === String(id));
              patch({ assignee_id: id ? Number(id) : null, assignee_name: u?.staff_name || null });
            }}
            style={SELECT}>
            <option value="">Unassigned</option>
            {assignableUsers.map(u => (
              <option key={u.admin_id} value={u.admin_id}>{u.staff_name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={FIELD_LABEL}>Area</div>
          <select
            value={task.area_id || ''}
            onChange={e => patch({ area_id: e.target.value ? Number(e.target.value) : null })}
            style={SELECT}>
            <option value="">No area</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        {!task.parent_task_id && (
          <div>
            <div style={FIELD_LABEL}>Epic</div>
            <select
              value={task.epic_id || ''}
              onChange={e => patch({ epic_id: e.target.value ? Number(e.target.value) : null })}
              style={{ ...SELECT, color: task.epic_color || 'inherit', fontWeight: task.epic_id ? 600 : 400 }}>
              <option value="">No epic</option>
              {epics.map(ep => <option key={ep.id} value={ep.id}>{ep.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <div style={FIELD_LABEL}>Due Date</div>
          <input
            type="date"
            value={task.due_date ? String(task.due_date).slice(0, 10) : ''}
            onChange={e => patch({ due_date: e.target.value || null })}
            style={{ ...SELECT, color: due?.overdue ? '#dc2626' : 'inherit' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={FIELD_LABEL}>Milestone</div>
          <input
            type="text"
            defaultValue={task.milestone || ''}
            onBlur={e => { const v = e.target.value.trim(); if (v !== (task.milestone || '')) patch({ milestone: v || null }); }}
            placeholder="e.g. Sandbox → Prod"
            style={{ ...SELECT, width: '100%' }}
          />
        </div>
        {saving && <span style={{ fontSize: 11, color: 'var(--gray-500)' }}><Save size={12} /> saving…</span>}
      </div>

      {task.status === 'blocked' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 12, marginBottom: 18 }}>
          <div style={FIELD_LABEL}>Blocked Reason</div>
          <textarea
            defaultValue={task.blocked_reason || ''}
            onBlur={e => { const v = e.target.value.trim(); if (v !== (task.blocked_reason || '')) patch({ blocked_reason: v || null }); }}
            placeholder="What's blocking this?"
            style={{ width: '100%', padding: 8, border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minHeight: 60, resize: 'vertical', outline: 'none' }}
          />
        </div>
      )}

      {/* Description */}
      <div style={{ marginBottom: 24 }}>
        <div style={FIELD_LABEL}>Description</div>
        <ReactQuill
          theme="snow"
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          value={descEdit}
          onChange={(v) => { setDescEdit(v); setDescDirty(true); }}
          onBlur={saveDescription}
        />
        {descDirty && (
          <button
            onClick={saveDescription}
            style={{ marginTop: 6, padding: '6px 12px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Save description
          </button>
        )}
      </div>

      {/* Subtasks */}
      {!task.parent_task_id && (
        <Section
          icon={<CheckSquare size={14} />}
          title="Subtasks"
          count={data.subtasks.length}
          open={showSubtasks}
          onToggle={() => setShowSubtasks(v => !v)}>
          {data.subtasks.map(sub => (
            <div key={sub.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderBottom: '1px solid var(--gray-100)',
            }}>
              <button
                onClick={() => toggleSubtaskDone(sub)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'inline-flex' }}>
                {sub.status === 'done'
                  ? <CheckSquare size={16} color="#15803d" />
                  : <Square size={16} color="var(--gray-400)" />}
              </button>
              <span style={{
                flex: 1, fontSize: 13,
                textDecoration: sub.status === 'done' ? 'line-through' : 'none',
                color: sub.status === 'done' ? 'var(--gray-500)' : 'var(--gray-800)',
              }}>{sub.title}</span>
              {sub.assignee_name && (
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>👤 {sub.assignee_name}</span>
              )}
              {sub.due_date && (
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                  <Clock size={10} /> {new Date(sub.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 0' }}>
            <input
              value={newSubtask}
              onChange={e => setNewSubtask(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addSubtask(); }}
              placeholder="Add a subtask"
              style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none' }}
            />
            <button
              onClick={addSubtask}
              disabled={!newSubtask.trim()}
              style={{ padding: '7px 12px', background: newSubtask.trim() ? 'var(--orange)' : 'var(--gray-300)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: newSubtask.trim() ? 'pointer' : 'default' }}>
              <Plus size={12} /> Add
            </button>
          </div>
        </Section>
      )}

      {/* Comments */}
      <Section
        icon={<MessageSquare size={14} />}
        title="Comments"
        count={data.comments.length}
        open={showComments}
        onToggle={() => setShowComments(v => !v)}>
        <div style={{ marginBottom: 12 }}>
          <ReactQuill
            theme="snow"
            modules={COMMENT_MODULES}
            formats={COMMENT_FORMATS}
            value={newComment}
            onChange={setNewComment}
            placeholder="Add a comment…"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              onClick={postComment}
              disabled={postingComment}
              style={{ padding: '7px 14px', background: postingComment ? 'var(--gray-300)' : 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: postingComment ? 'wait' : 'pointer' }}>
              {postingComment ? 'Posting…' : 'Post comment'}
            </button>
          </div>
        </div>
        {data.comments.map(c => (
          <div key={c.id} style={{ padding: '10px 0', borderTop: '1px solid var(--gray-100)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy)' }}>{c.admin_name || 'Someone'}</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{fmt(c.created_at)}</span>
              {c.admin_id === user?.id && (
                <button
                  onClick={() => deleteComment(c.id)}
                  title="Delete"
                  style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--gray-400)', cursor: 'pointer' }}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--gray-800)' }} dangerouslySetInnerHTML={{ __html: c.body }} />
          </div>
        ))}
        {!data.comments.length && (
          <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '6px 0' }}>No comments yet.</div>
        )}
      </Section>

      {/* Links */}
      <Section
        icon={<Link2 size={14} />}
        title="Links"
        count={data.links.length}
        open={showLinks}
        onToggle={() => setShowLinks(v => !v)}>
        {data.links.map(l => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--gray-100)' }}>
            <ExternalLink size={13} color="var(--gray-500)" />
            <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--navy)', textDecoration: 'none', fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.label || l.url}
            </a>
            <button onClick={() => deleteLink(l.id)} style={{ background: 'transparent', border: 'none', color: 'var(--gray-400)', cursor: 'pointer' }}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, padding: '8px 0' }}>
          <input
            value={newLink.label}
            onChange={e => setNewLink(s => ({ ...s, label: e.target.value }))}
            placeholder="Label (optional)"
            style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none' }}
          />
          <input
            value={newLink.url}
            onChange={e => setNewLink(s => ({ ...s, url: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') addLink(); }}
            placeholder="https://…"
            style={{ flex: 2, padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, outline: 'none' }}
          />
          <button
            onClick={addLink}
            disabled={!newLink.url.trim()}
            style={{ padding: '7px 12px', background: newLink.url.trim() ? 'var(--orange)' : 'var(--gray-300)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: newLink.url.trim() ? 'pointer' : 'default' }}>
            Add
          </button>
        </div>
      </Section>

      {/* Attachments */}
      <Section
        icon={<Paperclip size={14} />}
        title="Attachments"
        count={data.attachments.length}
        open={showAttachments}
        onToggle={() => setShowAttachments(v => !v)}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
          {data.attachments.map(a => {
            const thumb = r2Url(a.r2_thumb_key) || r2Url(a.r2_original_key);
            return (
              <div key={a.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden', background: '#fff', position: 'relative' }}>
                {thumb ? (
                  <img src={thumb} alt={a.alt_text || ''} style={{ display: 'block', width: '100%', height: 100, objectFit: 'cover' }} />
                ) : (
                  <div style={{ height: 100, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageIcon size={20} color="var(--gray-400)" />
                  </div>
                )}
                <div style={{ padding: 6, fontSize: 11, color: 'var(--gray-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.suggested_name || `#${a.media_asset_id}`}
                </div>
                <button
                  onClick={() => unlinkAttachment(a.id)}
                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,0.85)', border: '1px solid var(--gray-300)', borderRadius: 4, padding: 3, cursor: 'pointer' }}>
                  <X size={11} />
                </button>
              </div>
            );
          })}
        </div>
        <button
          onClick={() => setPickerOpen(true)}
          style={{ padding: '7px 12px', background: 'transparent', border: '1px dashed var(--gray-300)', borderRadius: 6, fontSize: 12, color: 'var(--gray-600)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Plus size={12} /> Attach from Media Library
        </button>
        {pickerOpen && (
          <MediaPicker
            multiSelect
            onClose={() => setPickerOpen(false)}
            onPick={onPickAttachments}
          />
        )}
      </Section>

      {/* Activity */}
      <Section
        icon={<Activity size={14} />}
        title="Activity"
        count={data.activity.length}
        open={showActivity}
        onToggle={() => setShowActivity(v => !v)}>
        {data.activity.map(a => (
          <div key={a.id} style={{ padding: '6px 0', fontSize: 12, color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)' }}>
            <strong style={{ color: 'var(--navy)' }}>{a.admin_name || 'Someone'}</strong>{' '}
            <span>{describeActivity(a)}</span>{' · '}
            <span style={{ color: 'var(--gray-400)' }}>{fmt(a.created_at)}</span>
          </div>
        ))}
        {!data.activity.length && (
          <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '6px 0' }}>No activity yet.</div>
        )}
      </Section>
    </div>
  );
}

function describeActivity(a) {
  const arrow = (a.from_value && a.to_value) ? `: ${a.from_value} → ${a.to_value}` : (a.to_value ? `: ${a.to_value}` : '');
  switch (a.kind) {
    case 'created':         return 'created the task' + arrow;
    case 'status_change':   return 'changed status' + arrow;
    case 'priority_change': return 'changed priority' + arrow;
    case 'assignee_change': return 'changed assignee' + arrow;
    case 'due_change':      return 'changed due date' + arrow;
    case 'area_change':     return 'moved to a different area' + arrow;
    case 'epic_change':     return 'changed epic' + arrow;
    default:                return a.kind + arrow;
  }
}

function Section({ icon, title, count, open, onToggle, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 0', cursor: 'pointer', userSelect: 'none',
          borderBottom: '1px solid var(--gray-200)', marginBottom: 8,
        }}>
        {open ? <ChevronDown size={13} color="var(--gray-500)" /> : <ChevronRight size={13} color="var(--gray-500)" />}
        {icon}
        <span style={{ fontWeight: 700, fontSize: 12.5, color: 'var(--navy)' }}>{title}</span>
        {count !== undefined && (
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', background: 'var(--gray-100)', padding: '1px 7px', borderRadius: 8 }}>{count}</span>
        )}
      </div>
      {open && children}
    </div>
  );
}
